import React, { useState, useEffect, useMemo } from 'react';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import { Container, Row, Col, Card, Table, Spinner, Badge, Form, Button } from 'react-bootstrap';
import { FaMoneyBillWave, FaUsers, FaCheckCircle, FaSearch } from 'react-icons/fa';
import axios from 'axios';
import BaseUrl from '../../ApiServices/BaseUrl';

const roleLabels = { employer: 'Employer', jobseeker: 'Job Seeker', agency: 'Agency' };
const paymentBadge = { paid: 'success', coupon_applied: 'info', pending: 'warning', free: 'secondary' };

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

export default function AdminPayments() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [role, setRole] = useState('');
    const [paymentStatus, setPaymentStatus] = useState('paid');
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams();
        if (role) params.set('role', role);
        if (paymentStatus) params.set('payment_status', paymentStatus);
        if (search) params.set('search', search);
        axios.get(`${BaseUrl}/api/admin/payments?${params.toString()}`)
            .then(res => setRows(res.data.data || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [role, paymentStatus, search]);

    // Revenue grouped by currency since plans are sold in multiple currencies
    const revenueByCurrency = useMemo(() => {
        const totals = {};
        rows.filter(r => r.payment_status === 'paid').forEach(r => {
            const cur = r.currency || 'N/A';
            totals[cur] = (totals[cur] || 0) + Number(r.amount || 0);
        });
        return totals;
    }, [rows]);

    const activeCount = rows.filter(r => r.status === 'active').length;

    const handleSearch = (e) => {
        e.preventDefault();
        setSearch(searchInput.trim());
    };

    return (
        <>
            <AdminNavbar />
            <Container fluid style={{ marginTop: '90px', paddingLeft: '24px', paddingRight: '24px' }}>
                <h2 className="fw-bold mb-4 d-flex align-items-center gap-2">
                    <FaMoneyBillWave className="text-success" /> Payments &amp; Subscribers
                </h2>

                <Row className="mb-4 g-3">
                    <Col xs={12} sm={6} md={4}>
                        <Card className="text-center border-0 shadow-sm h-100">
                            <Card.Body>
                                <FaUsers className="text-primary mb-2" size={24} />
                                <h3 className="fw-bold mb-0">{loading ? '--' : rows.length}</h3>
                                <div className="text-muted">Records Shown</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} sm={6} md={4}>
                        <Card className="text-center border-0 shadow-sm h-100">
                            <Card.Body>
                                <FaCheckCircle className="text-success mb-2" size={24} />
                                <h3 className="fw-bold mb-0">{loading ? '--' : activeCount}</h3>
                                <div className="text-muted">Currently Active</div>
                            </Card.Body>
                        </Card>
                    </Col>
                    <Col xs={12} md={4}>
                        <Card className="text-center border-0 shadow-sm h-100">
                            <Card.Body>
                                <FaMoneyBillWave className="text-warning mb-2" size={24} />
                                <h3 className="fw-bold mb-0">
                                    {loading ? '--' : Object.keys(revenueByCurrency).length === 0 ? '0' :
                                        Object.entries(revenueByCurrency).map(([cur, amt]) => `${cur} ${amt.toLocaleString()}`).join(' | ')}
                                </h3>
                                <div className="text-muted">Paid Revenue (shown records)</div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-3">
                    <Card.Body>
                        <Row className="g-2 align-items-end">
                            <Col xs={12} md={3}>
                                <Form.Label className="small text-muted mb-1">User Type</Form.Label>
                                <Form.Select value={role} onChange={e => setRole(e.target.value)}>
                                    <option value="">All</option>
                                    <option value="employer">Employer</option>
                                    <option value="jobseeker">Job Seeker</option>
                                    <option value="agency">Agency</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} md={3}>
                                <Form.Label className="small text-muted mb-1">Payment Status</Form.Label>
                                <Form.Select value={paymentStatus} onChange={e => setPaymentStatus(e.target.value)}>
                                    <option value="paid">Paid</option>
                                    <option value="coupon_applied">Coupon Applied</option>
                                    <option value="pending">Pending</option>
                                    <option value="free">Free</option>
                                    <option value="">All</option>
                                </Form.Select>
                            </Col>
                            <Col xs={12} md={6}>
                                <Form onSubmit={handleSearch} className="d-flex gap-2">
                                    <Form.Control
                                        placeholder="Search name, email, phone, plan or Razorpay ID"
                                        value={searchInput}
                                        onChange={e => setSearchInput(e.target.value)}
                                    />
                                    <Button type="submit" variant="primary"><FaSearch /></Button>
                                </Form>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm mb-5">
                    <Card.Body className="p-0">
                        {loading ? (
                            <div className="text-center py-5"><Spinner animation="border" /></div>
                        ) : rows.length === 0 ? (
                            <div className="text-center py-5 text-muted">No records found</div>
                        ) : (
                            <Table responsive hover className="mb-0 align-middle">
                                <thead className="table-light">
                                    <tr>
                                        <th>#</th>
                                        <th>Name</th>
                                        <th>Contact</th>
                                        <th>Type</th>
                                        <th>Plan</th>
                                        <th>Amount</th>
                                        <th>Payment</th>
                                        <th>Status</th>
                                        <th>Start</th>
                                        <th>Expiry</th>
                                        <th>Razorpay ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={`${r.source}-${r.id}`}>
                                            <td>{i + 1}</td>
                                            <td className="fw-semibold">{`${r.first_name || ''} ${r.last_name || ''}`.trim() || '-'}</td>
                                            <td>
                                                <div>{r.email || '-'}</div>
                                                <small className="text-muted">{r.mobile_number || ''}</small>
                                            </td>
                                            <td>{roleLabels[r.role] || r.role || '-'}</td>
                                            <td>
                                                {r.plan_name || '-'}
                                                {r.source === 'legacy_user_plan' && <Badge bg="light" text="dark" className="ms-1">Old</Badge>}
                                            </td>
                                            <td>{r.amount != null ? `${r.currency || ''} ${Number(r.amount).toLocaleString()}` : '-'}</td>
                                            <td><Badge bg={paymentBadge[r.payment_status] || 'secondary'}>{r.payment_status || '-'}</Badge></td>
                                            <td>{r.status || '-'}</td>
                                            <td>{formatDate(r.start_date)}</td>
                                            <td>{formatDate(r.expiry_date)}</td>
                                            <td><small>{r.razorpay_payment_id || r.razorpay_order_id || '-'}</small></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
}
